/*!
 * CHIEF - Cybersecurity Learning Platform (PROPRIETARY SOFTWARE)
 * Login Page - CHIEF Platform
 * 
 * Copyright (c) 2025 Srivatsan Sk. All rights reserved.
 * 
 * PROPRIETARY LICENSE NOTICE:
 * This software is the exclusive proprietary property of Srivatsan Sk.
 * Licensed to client under strict commercial license agreement.
 * Unauthorized use, copying, modification, distribution, or removal
 * of copyright notices is strictly prohibited.
 * 
 * Original Author: Srivatsan Sk
 * License: Proprietary/Commercial
 * Status: Confidential
 * 
 * WARNING: This code contains proprietary business logic and algorithms.
 * Reverse engineering, decompilation, or derivative works are prohibited.
 */

// src/pages/Login.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { useAuth } from '../contexts/AuthContext';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing[4]};
  position: relative;
  overflow: hidden;
`;

const ParticlesContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  padding: ${({ theme }) => theme.spacing[6]};
  background: rgba(0, 210, 255, 0.05);
  backdrop-filter: blur(5px);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid rgba(0, 210, 255, 0.1);
  max-width: 500px;
  width: 100%;
`;

const WelcomeText = styled.p`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  font-weight: 300;
  line-height: 1.6;
`;

const SecurityBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  background: rgba(0, 210, 255, 0.1);
  border: 1px solid rgba(0, 210, 255, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  
  svg {
    color: ${({ theme }) => theme.colors.success};
  }
`;

const Logo = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes['4xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  letter-spacing: 1px;
  text-shadow: 0 0 20px rgba(0, 210, 255, 0.5);
  position: relative;
  z-index: 3;
`;

const LoginCard = styled.div`
  width: 100%;
  max-width: 500px;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  background: rgba(18, 24, 38, 0.85);
  backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[8]};
  position: relative;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium}, 0 8px 32px rgba(0, 210, 255, 0.1);
  border: 1px solid rgba(0, 210, 255, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 600;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.primary};
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.glow};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const ForgotPassword = styled(Link)`
  display: block;
  text-align: right;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${({ theme }) => theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const RegisterLink = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  a {
    color: ${({ theme }) => theme.colors.primary};
    margin-left: ${({ theme }) => theme.spacing[1]};
    font-weight: 500;
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(10, 255, 112, 0.1);
  color: ${({ theme }) => theme.colors.success};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.success};
`;

const ErrorMessage = styled.div`
  background-color: rgba(220, 53, 69, 0.1);
  color: ${({ theme }) => theme.colors.error};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.error};
  font-weight: 500;
`;

const Footer = styled.footer`
  margin-top: ${({ theme }) => theme.spacing[8]};
  padding: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  text-align: center;
  background: rgba(0, 210, 255, 0.02);
  backdrop-filter: blur(5px);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid rgba(0, 210, 255, 0.05);
  max-width: 400px;
  width: 100%;
`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Stealth copyright - developed by Srivatsan Sk
  React.useEffect(() => {
    if (window.__CHIEF_COPYRIGHT) return;
    window.__CHIEF_COPYRIGHT = true;
    console.log('%c🔐 CHIEF Login System | Created By Srivatsan Sk', 'background: linear-gradient(90deg, #00d2ff, #667eea); color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;');
  }, []);

  // Initialize particles
  const particlesInit = useCallback(async engine => {
    await loadSlim(engine);
  }, []);

  const particlesOptions = {
    background: {
      color: {
        value: "transparent",
      },
    },
    fpsLimit: 60,
    interactivity: {
      events: {
        onClick: {
          enable: true,
          mode: "push",
        },
        onHover: {
          enable: true,
          mode: "repulse",
        },
        resize: true,
      },
      modes: {
        push: {
          quantity: 2,
        },
        repulse: {
          distance: 80,
          duration: 0.4,
        },
      },
    },
    particles: {
      color: {
        value: "#00d2ff",
      },
      links: {
        color: "#00d2ff",
        distance: 140,
        enable: true,
        opacity: 0.2,
        width: 1,
      },
      collisions: {
        enable: true,
      },
      move: {
        direction: "none",
        enable: true,
        outModes: {
          default: "bounce",
        },
        random: false,
        speed: 0.6,
        straight: false,
      },
      number: {
        density: {
          enable: true,
          area: 900,
        },
        value: 50,
      },
      opacity: {
        value: 0.3,
      },
      shape: {
        type: "circle",
      },
      size: {
        value: { min: 1, max: 2 },
      },
    },
    detectRetina: true,
  };

  // Extract success message from location state
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Function to display a global error message
  const showGlobalError = (message) => {
    // Remove existing error if any
    const existingError = document.getElementById('global-error-popup');
    if (existingError) {
      // document.body.removeChild(existingError);
    }

    // Create new error popup element
    const errorPopup = document.createElement('div');
    errorPopup.id = 'global-error-popup';

    // Style the popup
    Object.assign(errorPopup.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      backgroundColor: '#dc3545',
      color: 'white',
      padding: '12px 20px',
      borderRadius: '6px',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
      zIndex: '9999',
      maxWidth: '350px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      opacity: '0',
      transform: 'translateX(30px)',
      transition: 'all 0.3s ease'
    });

    // Create close button
    const closeButton = document.createElement('span');
    closeButton.innerHTML = '&times;';
    Object.assign(closeButton.style, {
      marginLeft: '15px',
      fontSize: '20px',
      cursor: 'pointer',
      fontWeight: 'bold'
    });

    // Add close functionality
    closeButton.onclick = function () {
      errorPopup.style.opacity = '0';
      errorPopup.style.transform = 'translateX(30px)';
      setTimeout(() => {
        if (document.body.contains(errorPopup)) {
          document.body.removeChild(errorPopup);
        }
      }, 300);
    };

    // Set the message
    const messageSpan = document.createElement('span');
    messageSpan.innerText = message;
    errorPopup.appendChild(messageSpan);
    errorPopup.appendChild(closeButton);

    // Add to the DOM
    document.body.appendChild(errorPopup);

    // Trigger animation
    setTimeout(() => {
      errorPopup.style.opacity = '1';
      errorPopup.style.transform = 'translateX(0)';
    }, 10);

    // Auto dismiss after 10 seconds
    setTimeout(() => {
      if (document.body.contains(errorPopup)) {
        errorPopup.style.opacity = '0';
        errorPopup.style.transform = 'translateX(30px)';
        setTimeout(() => {
          if (document.body.contains(errorPopup)) {
            document.body.removeChild(errorPopup);
          }
        }, 300);
      }
    }, 10000);
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();

  //   // Validate inputs first before clearing any messages
  //   if (!email || !password) {
  //     const validationError = 'Please fill in all fields';
  //     setError(validationError);
  //     showGlobalError(validationError);
  //     return;
  //   }

  //   // Clear previous messages only after validation passes
  //   setSuccess('');
  //   setError('');
  //   setLoading(true);

  //   try {
  //     // Call the login function
  //     const response = await login({ email, password });

  //     // Check response
  //     if (response && response.success) {
  //       // Check if password change is required
  //       if (response.passwordChangeRequired) {
  //         setSuccess('Login successful! You must change your password for security purposes.');
  //         // Redirect to password change page after a short delay
  //         setTimeout(() => {
  //           navigate('/change-password', {
  //             state: {
  //               fromLogin: true,
  //               message: 'Please change your password to continue'
  //             }
  //           });
  //         }, 2000);
  //         return;
  //       }

  //       // Normal success path - navigate immediately
  //       navigate('/');
  //     } else {
  //       // Error handling path - response.success is false
  //       let errorMessage = 'Login failed. Please check your credentials.';

  //       // Extract error message from response
  //       if (response && response.error) {
  //         errorMessage = typeof response.error === 'string' ? response.error : errorMessage;
  //       } else if (response && response.message) {
  //         errorMessage = response.message;
  //       }

  //       // Set error state and show global error
  //       setError(errorMessage);
  //       showGlobalError(errorMessage);
  //       setLoading(false); // Stop loading on error
  //     }
  //   } catch (err) {
  //     console.error("Login error (caught exception):", err);

  //     // Extract error message from exception
  //     let errorMessage = 'An unexpected error occurred. Please try again.';

  //     if (err && err.message) {
  //       errorMessage = err.message;
  //     } else if (err && err.response && err.response.data) {
  //       if (typeof err.response.data === 'object' && err.response.data.detail) {
  //         errorMessage = err.response.data.detail;
  //       } else if (typeof err.response.data === 'object' && err.response.data.message) {
  //         errorMessage = err.response.data.message;
  //       } else if (typeof err.response.data === 'string') {
  //         errorMessage = err.response.data;
  //       }
  //     }

  //     // Set error state and show global error
  //     setError(errorMessage);
  //     showGlobalError(errorMessage);
  //     setLoading(false); // Stop loading on error
  //   }
  // };
  // Replace your handleSubmit function with this improved version:

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Validate inputs
    if (!email || !password) {
      const validationError = 'Please fill in all fields';
      setError(validationError);
      showGlobalError(validationError);
      return;
    }

    // Clear previous messages and start loading
    setSuccess('');
    setError('');
    setLoading(true);

    try {
      const response = await login({ email, password });

      // Handle null/undefined response
      if (!response) {
        throw new Error('No response from server. Please try again.');
      }

      // Success path
      if (response.success === true) {
      // Handle password change requirement
        if (response.passwordChangeRequired) {
          setSuccess('Login successful! You must change your password for security purposes.');
          setLoading(false); // Reset loading before navigation

          setTimeout(() => {
            navigate('/change-password', {
              state: {
                fromLogin: true,
                message: 'Please change your password to continue'
              }
            });
          }, 2000);
          return;
        }

        // Normal success - reset loading before navigation
        setLoading(false);
        navigate('/');
        return;
      }

      // Error path - response.success is false or missing
      let errorMessage = 'Login failed. Please check your credentials.';

      if (response.error) {
        errorMessage = typeof response.error === 'string'
          ? response.error
          : errorMessage;
      } else if (response.message) {
        errorMessage = response.message;
      }

      setError(errorMessage);
      showGlobalError(errorMessage);

    } catch (err) {
      console.error("Login error:", err);

      // Extract error message from exception
      let errorMessage = 'An unexpected error occurred. Please try again.';

      if (err?.message) {
        errorMessage = err.message;
      } else if (err?.response?.data) {
        const data = err.response.data;
        if (typeof data === 'object') {
          errorMessage = data.detail || data.message || errorMessage;
        } else if (typeof data === 'string') {
          errorMessage = data;
        }
      }

      setError(errorMessage);
      showGlobalError(errorMessage);

    } finally {
      // Always reset loading state
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <ParticlesContainer>
        <Particles
          id="tsparticles"
          init={particlesInit}
          options={particlesOptions}
        />
      </ParticlesContainer>

      <ContentWrapper>
        <Logo>C.H.I.E.F</Logo>


        <LoginCard>
          <Title>Access Restricted</Title>
          <Subtitle>Login to continue your cyber security journey</Subtitle>

          {/* Display error messages */}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {/* Only show success messages in-form */}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <PasswordInputWrapper>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </PasswordToggle>
              </PasswordInputWrapper>
            </FormGroup>

            <ForgotPassword to="/forgot-password">Forgot password?</ForgotPassword>

            <LoginButton type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </LoginButton>

            <RegisterLink>
              Don't have an account?<Link to="/register">Register</Link>
            </RegisterLink>
          </Form>
        </LoginCard>

        <Footer>
          © {new Date().getFullYear()} C.H.I.E.F. All rights reserved.
        </Footer>
      </ContentWrapper>
    </LoginContainer>
  );
};

export default Login;